import { useMemo, useState } from "react";
import moment from "moment-timezone";
import { CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { CommodityOptions } from "@/lib/api/commodity";
import {
  useListHistoricalData,
  HistoricalData,
  HistoricalMessageReceiver,
  InternalUser,
  BaseInfo,
  useSendAdhocMessage,
} from "@/lib/api/destination";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { Label } from "@/components/ui/label";
import { AdhocMessageModal } from "./AdhocMessageModal";

interface HistoricalViewProps {
  customers: BaseInfo["customers"];
  marketZones: BaseInfo["market_zones"];
  internalUsers: InternalUser[];
}

export function HistoricalView({ customers, marketZones, internalUsers }: HistoricalViewProps) {
  const [sentDate, setSentDate] = useState<Date>(new Date());
  const [selectedRowData, setSelectedRowData] = useState<{
    content_id: string;
    destination_id: string;
    commodity: string;
    filteredReceivers: Array<{
      receiver_id?: string;
      internal_user_id?: string;
      receiver_name: string;
      user_type: "freight" | "internal";
    }>;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const historicalDataRes = useListHistoricalData(moment(sentDate).format("YYYY-MM-DD"));
  const loadingOrErrorUi = GenerateLoadingOrError([historicalDataRes]);

  const { messages: historicalRows } = historicalDataRes.data?.data || { messages: [], internal_users: [] };

  // Flatten the nested data structure for DataTable
  const flattenedRows = useMemo(() => {
    const rows: Array<{
      id: string;
      destination_id: string;
      destination_name: string;
      original_commodity: string;
      commodity: string;
      commodity_display: string;
      receiver_id?: string;
      internal_user_id?: string;
      receiver_name: string;
      user_type: "freight" | "internal";
      to_address?: string;
      message: string;
      created_at?: string | Date;
      updated_at?: string | Date;
      content_id: string;
    }> = [];

    historicalRows.forEach((historicalMessage: HistoricalData) => {
      const destination = marketZones.find(mz => mz.id === historicalMessage.destination_id);
      const message = historicalMessage.message || historicalMessage?.content?.meta_data?.body || "";

      historicalMessage.DestinationMessageReceivers.forEach((receiver: HistoricalMessageReceiver) => {
        const commoditySplit = receiver.commodity.split("_");

        const receiverName = receiver.receiver_id
          ? customers.find((customer: any) => customer.id === receiver.receiver_id)?.long_name || "Unknown Customer"
          : internalUsers.find((user: InternalUser) => user.id === receiver.internal_user_id)?.name || "Unknown User";

        rows.push({
          id: `${receiver.id}_${receiver.commodity}`,
          destination_id: historicalMessage.destination_id,
          destination_name: destination?.dtn_market_zone || "Unknown Destination",
          original_commodity: receiver.commodity,
          commodity: receiver.commodity,
          commodity_display: commoditySplit.map(commodity => CommodityOptions.find((option: any) => option.value === commodity)?.label || commodity).join(" & "),
          receiver_id: receiver.receiver_id,
          internal_user_id: receiver.internal_user_id,
          receiver_name: receiverName,
          user_type: receiver.receiver_id ? "freight" : "internal",
          to_address: receiver.to_address,
          message: message,
          created_at: receiver.created_at,
          updated_at: receiver.updated_at,
          content_id: historicalMessage.content_id,
        });
      });
    });

    return rows;
  }, [historicalRows, marketZones, customers, internalUsers]);

  const handleRowClick = (row: typeof flattenedRows[0]) => {
    // Filter all rows with the same content_id
    const filteredByContentId = flattenedRows.filter(r => r.content_id === row.content_id);

    // Get unique receivers
    const uniqueReceivers = new Map();
    filteredByContentId.forEach(r => {
      const key = r.receiver_id || r.internal_user_id;
      if (key && !uniqueReceivers.has(key)) {
        uniqueReceivers.set(key, {
          receiver_id: r.receiver_id,
          internal_user_id: r.internal_user_id,
          receiver_name: r.receiver_name,
          user_type: r.user_type,
        });
      }
    });

    setSelectedRowData({
      content_id: row.content_id,
      destination_id: row.destination_id,
      commodity: row.commodity,
      filteredReceivers: Array.from(uniqueReceivers.values()),
    });
    setIsModalOpen(true);
  };

  if (loadingOrErrorUi) {
    return loadingOrErrorUi;
  }

  if (!sentDate) {
    return (
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-row justify-end items-center">
          <span className="flex flex-col gap-2">
            <Label>Select a date to view historical messages</Label>
          </span>
        </div>
      </div>
    );
  }

  const columns: DataTableColumn<typeof flattenedRows[0]>[] = [
    {
      id: "destination_name",
      accessorKey: "destination_name",
      header: "Destination",
      cell: (info) => info.getValue(),
    },
    {
      id: "commodity_display",
      accessorKey: "commodity_display",
      header: "Commodity",
      cell: ({ row }) => row.original.commodity_display,
    },
    {
      id: "original_commodity",
      accessorKey: "original_commodity",
      header: "Selected Commodity",
      cell: ({ row }) => row.original.original_commodity.split("_").join(" & "),
    },
    {
      id: "receiver_name",
      accessorKey: "receiver_name",
      header: "Receiver",
      cell: ({ row }) => row.original.receiver_name,
    },
    {
      id: "user_type",
      accessorKey: "user_type",
      header: "Type",
      cell: ({ row }) => row.original.user_type === "freight" ? "Freight/Producer" : "Internal User",
    },
    {
      id: "to_address",
      accessorKey: "to_address",
      header: "Phone",
      cell: ({ row }) => row.original.to_address || "",
    },
    {
      id: "message",
      accessorKey: "message",
      header: "Message",
      enableTooltip: true,
      cell: ({ row }) => row.original.message,
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Sent",
      cell: (info) => moment.tz(info.getValue(), CommonDestinationTimezone).format("YYYY-MM-DD hh:mm a"),
    },
  ];

  return (
    <>
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-row justify-start items-center">
          <span className="flex flex-col gap-2">
            <Label>Message Date</Label>
            <DatePicker
              value={sentDate}
              onChange={(date) => {
                setSentDate(date as Date);
              }}
              placeholder=""
              className="w-[200px]"
            />
          </span>
        </div>
        {historicalRows.length ? (
          <DataTable
            className="shadow-lg"
            stickyHeaders
            tableClassName="max-h-[60vh] overflow-y-scroll"
            data={flattenedRows}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            filterStyle='select'
            filterMode='internal'
            groupBy={[
              "destination_name",
              "commodity_display"
            ]}
            defaultSorting={[
              { id: "destination_name", desc: false },
              { id: "message", desc: false },
              { id: "commodity_display", desc: false },
              { id: "user_type", desc: false },
              { id: "receiver_name", desc: false },
            ]}
            emptyMessage="No historical messages found"
            rowClickable
            onRowClick={handleRowClick}
          />
        ) : (
          <div className="mt-4 text-center text-muted-foreground">
            Found no messages for selected date
          </div>
        )}
      </div>

      {selectedRowData && (
        <AdhocMessageModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedRowData={selectedRowData}
        />
      )}
    </>
  );
}
